import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/chrome/PageHeader';
import HelpPopover, { FaqEntry } from '../../components/chrome/HelpPopover';
import TickerPills from '../../components/chrome/TickerPills';
import SymbolSearch from '../../components/chrome/SymbolSearch';
import HeatmapPanels from '../../components/heatmap/HeatmapPanels';
import StarNodesStrip from '../../components/heatmap/StarNodesStrip';
import HeatmapControls from '../../components/heatmap/HeatmapControls';
import HeatmapTable from '../../components/heatmap/HeatmapTable';
import HeatmapMobile from '../../components/heatmap/HeatmapMobile';
import HeatmapEmpty from '../../components/heatmap/HeatmapEmpty';
import ComparePanels from '../../components/heatmap/ComparePanels';
import Disclaimer from '../../components/chrome/Disclaimer';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useHeatmapData } from '../../hooks/useHeatmapData';
import { useTickers } from '../../hooks/useTickers';
import { useHashTicker } from '../../hooks/useHashTicker';
import { useStrikeWindow } from '../../hooks/useStrikeWindow';
import { useExpirationLimit } from '../../hooks/useExpirationLimit';
import { useVisualSettings } from '../../hooks/useVisualSettings';
import { useRecentTickers } from '../../hooks/useRecentTickers';
import { useAuth } from '../../hooks/useAuth';
import { HeatmapFrame, ViewMode } from '../../lib/types';
import { DEFAULT_TICKER, STANDARD_INDICES, STORAGE_KEYS } from '../../lib/constants';

const MOBILE_BREAKPOINT = 720;

// Age thresholds — mirrors useAmbientTelemetry's connection taxonomy so
// the heatmap page and the ambient strip use the same freshness model.
//   STREAMING : last frame < 90 s ago  (within one poll cycle)
//   STALE     : last frame < 5 min ago (one missed cycle)
//   OFFLINE   : no frame yet, or frame is genuinely old
// The old 24 h window let a sleeping laptop show LIVE on stale data.
const STREAMING_AFTER_MS = 90 * 1000;   //  90 seconds
const STALE_AFTER_MS     = 5 * 60 * 1000; //   5 minutes

type LiveStatus = 'STREAMING' | 'STALE' | 'OFFLINE';

/**
 * FAQ entries for the heatmap popover. Page-specific — covers what's
 * actually visible on /app/options. Definitional intro entries
 * (gamma, vanna, dealer hedging) live at the top so a first-timer can
 * read three short paragraphs and have enough context to interpret
 * everything else on the page.
 */
const HEATMAP_FAQ: FaqEntry[] = [
  {
    q: 'What is Gamma?',
    a: (
      <>
        <p>
          <strong>Gamma (Γ)</strong> is the rate at which an option's
          delta changes when the underlying price moves $1. Calls have
          positive gamma, puts have positive gamma, but a dealer who
          sells you those options ends up <em>short gamma</em> — and
          has to hedge dynamically to stay delta-neutral.
        </p>
        <p>
          That hedging is what creates magnets and cascade zones on
          this heatmap. <strong>Long gamma</strong> dealers sell into
          rallies and buy dips — they dampen moves (positive GEX,
          warm/palette colors). <strong>Short gamma</strong> dealers
          buy rallies and sell dips — they amplify moves (negative
          GEX, red/burn colors). The bigger the gamma stake at a
          strike, the more hedging volume to expect when price visits
          it.
        </p>
      </>
    ),
  },
  {
    q: 'What is Vanna?',
    a: (
      <>
        <p>
          <strong>Vanna (∂Δ/∂σ)</strong> is the rate at which an
          option's delta changes when implied volatility moves. It
          captures the cross-effect of vol on directional exposure —
          the reason vol shocks (a VIX spike, a sudden compression)
          can mechanically force dealer rebalancing even when spot
          hasn't moved much.
        </p>
        <p>
          On the heatmap, switching from <strong>GEX</strong> to{' '}
          <strong>VEX</strong> (top-right toggle) re-renders cells
          using vanna exposure instead of gamma. VEX walls show where
          dealers will need to buy or sell as IV regime changes —
          useful around earnings, FOMC, or any scheduled vol-event.
          Same color semantics, different greek.
        </p>
      </>
    ),
  },
  {
    q: 'How do I read a cell?',
    a: (
      <>
        <p>
          Each cell shows the dollar GEX at that strike × expiration. Color encodes both
          <strong> sign</strong> and <strong>magnitude</strong>:
        </p>
        <ul>
          <li><strong>Gold tint</strong> = positive GEX → dealers are <em>long gamma</em> here.
          They sell rallies and buy dips at this strike, dampening price moves.</li>
          <li><strong>Red tint</strong> = negative GEX → dealers are <em>short
          gamma</em>. They buy rallies and sell dips, amplifying moves.</li>
          <li><strong>Brighter</strong> = bigger absolute exposure. The brightest cells
          are where dealers have the most hedging to do.</li>
        </ul>
      </>
    ),
  },
  {
    q: "What's the spot row (white)?",
    a: 'The strike closest to the current underlying price. Everything above it is OTM calls + ITM puts; everything below is ITM calls + OTM puts. The heatmap auto-scrolls to keep this row visible.',
  },
  {
    q: "What's the King ★ (gold row)?",
    a: 'The strike with the largest absolute GEX anywhere on the chain — the single biggest dealer-positioning anchor. Sign-agnostic: the King could be either long-gamma (a pin wall that holds price in place) or short-gamma (a cascade wall that accelerates moves through it).',
  },
  {
    q: "What's the Counter-King ✦ (red row)?",
    a: (
      <>
        <p>
          The strongest strike with the <em>opposite sign</em> from the King. Direct
          counterforce: if the King is long-gamma (dampening), the Counter-King is the
          biggest short-gamma strike on the chain (amplifying), and vice versa.
        </p>
        <p>
          Fires only when both signs have significant exposure on the chain. In a
          monolithic regime (everything long-gamma, or everything short-gamma) the
          Counter-King simply doesn't appear — no false symmetry.
        </p>
      </>
    ),
  },
  {
    q: "What's the Gatekeeper ◆ (blue row)?",
    a: "A secondary wall sitting between spot and the King. Price has to push through these before reaching the King — they're the intermediate magnets/cascades on the path to the dominant level.",
  },
  {
    q: "What's the FLIP line (red dashed)?",
    a: 'The strike at which cumulative GEX crosses zero. Above it the regime is dampening (positive net gamma); below it the regime is amplifying (negative). Spot crossing the flip line is a regime change — moves get faster (or slower) in a hurry.',
  },
  {
    q: 'Why are negative GEX walls just as important as positive ones?',
    a: 'A long-gamma wall (positive) is where dealers will defend price — it pins. A short-gamma wall (negative) is where dealers will chase price — it accelerates. Breaching a big short-gamma strike often triggers a cascade because dealer hedging adds fuel rather than dampening it. Both are tactically critical; the heatmap colors negatives red to make them as visible as positives.',
  },
  {
    q: 'What are Magnets and Amps in the analytics card?',
    a: 'Magnets = strongest positive-GEX strikes above and below spot (the pin walls on each side). Amps = strongest negative-GEX strikes above and below spot (the cascade walls). The "front week" set restricts to the nearest expiration; the "full chain" set aggregates across all expirations included in the snapshot.',
  },
];

const Options: React.FC = () => {
  const { tickers, statuses, groups } = useTickers();
  // The standard index pills (SPY/SPX/QQQ/NDX/IWM) are always present and
  // first — they render immediately on mount, before /api/tickers resolves,
  // and survive a failed fetch. Anything the backend adds beyond the
  // standard set is appended (deduped, preserving backend order).
  const tickerList = useMemo(
    () => [...new Set<string>([...STANDARD_INDICES, ...tickers])],
    [tickers],
  );
  const [ticker, setTicker] = useHashTicker(tickerList, DEFAULT_TICKER);
  // Pill strip groups: a single EQUITIES section. The standard indices
  // are merged in so the row is never empty during load.
  const tickerGroups = useMemo(() => {
    const equities = [...new Set<string>([...STANDARD_INDICES, ...groups.equities])];
    return [
      { label: 'EQUITIES', tickers: equities },
    ];
  }, [groups.equities]);
  const { recent, favorites, pushRecent, removeRecent, toggleFavorite, isFavorite } = useRecentTickers();

  // ── Compare mode (Pro-only) ───────────────────────────────────────
  // Up to 3 side-by-side single-expiration ladders. Entered by dragging
  // a ticker pill/chip onto the drop zone (desktop) or the "+ Compare"
  // picker (mobile). Order is the render order; persisted so a reload
  // resumes the comparison. The page's primary ticker seeds slot 1.
  const { tier, isAdmin } = useAuth();
  const isPro = isAdmin || tier === 'pro';
  const [compareTickers, setCompareTickers] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.compareTickers);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string').slice(0, 3) : [];
    } catch { return []; }
  });
  const persistCompare = (next: string[]) => {
    setCompareTickers(next);
    try { localStorage.setItem(STORAGE_KEYS.compareTickers, JSON.stringify(next)); } catch { /* ignore */ }
  };
  const compareActive = isPro && compareTickers.length >= 2;
  const addCompare = (sym: string) => {
    const up = sym.trim().toUpperCase();
    if (!up || !isPro) return;
    const base = compareTickers.length ? compareTickers : [ticker];
    if (base.includes(up) || base.length >= 3) return;
    persistCompare([...base, up]);
  };
  const removeCompare = (sym: string) => {
    const next = compareTickers.filter(t => t !== sym);
    // Below 2 panels a comparison is meaningless — exit the mode.
    persistCompare(next.length >= 2 ? next : []);
  };
  // Desktop drag: pills/chips call this on dragstart so the drop zone
  // can appear; dragend always clears it (drop or cancel).
  const [tickerDragging, setTickerDragging] = useState(false);
  useEffect(() => {
    const end = () => setTickerDragging(false);
    window.addEventListener('dragend', end);
    return () => window.removeEventListener('dragend', end);
  }, []);
  const [viewMode, setViewMode] = useState<ViewMode>('gex');
  const [strikeWindow, setStrikeWindow] = useStrikeWindow();
  const [expLimit, setExpLimit] = useExpirationLimit();
  const { density } = useVisualSettings();

  const { latestFrame, isConnected, lastUpdateTime, switchTicker } = useWebSocket(ticker);

  // Live-only: the heatmap renders the latest streamed frame. (Historical
  // replay was sunset — see docs/REPLAY_PLAN.md for the Option C rebuild.)
  const activeFrame: HeatmapFrame | null = latestFrame;

  const heatmapData = useHeatmapData(activeFrame, viewMode, expLimit);

  // 1 s tick that ages the live freshness state (liveStatus) so the
  // spot-strip badge can fall STREAMING → STALE → OFFLINE even when no new
  // frame arrives. The wall-clock time display moved to the top ambient bar.
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Mobile breakpoint flag (drives which heatmap renderer mounts).
  // Initialise to false (SSR-safe) then correct after first paint so we
  // never access `window` during server-side rendering or static
  // prerender. Orientation changes are also caught so a portrait→landscape
  // flip on a tablet switches to the desktop table instantly.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    check(); // correct on mount
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  const alwaysOnSet = useMemo(
    () => new Set<string>(groups.equities),
    [groups.equities],
  );

  const handleTickerChange = (next: string) => {
    setTicker(next);
    switchTicker(next);
  };

  // Search-driven pick — bump the MRU list and switch the active ticker.
  // Always-on tickers (pills above) are excluded from the MRU list so
  // recent reflects "discovered via search", not every click.
  const handleSearchPick = (next: string) => {
    setTicker(next);
    switchTicker(next);
    if (!alwaysOnSet.has(next)) pushRecent(next);
  };

  const spotPrice = activeFrame?.spot_price ?? statuses[ticker]?.spot_price ?? null;
  const flipPoint = activeFrame?.gex_flip_point ?? null;
  const isMarketOpen = activeFrame?.is_market_open ?? true;

  // 3-state connection status — mirrors useAmbientTelemetry's taxonomy
  // so every status indicator on the page uses the same freshness model.
  // Derived from data age, not raw WS state, so transient reconnects
  // (NAT timeouts, wifi handoffs) don't flicker the chip.
  const liveStatus = useMemo((): LiveStatus => {
    if (!lastUpdateTime) return 'OFFLINE';
    const age = clock.getTime() - lastUpdateTime.getTime();
    if (age < STREAMING_AFTER_MS) return 'STREAMING';
    if (age < STALE_AFTER_MS)     return 'STALE';
    return 'OFFLINE';
  }, [clock, lastUpdateTime]);

  return (
    <div className="gv-options-page">
      <PageHeader
        title="OPTIONS HEATMAPS"
        subtitle="Dealer GEX & VEX exposure across strikes and expirations."
        help={
          <HelpPopover
            title="How to read the heatmap"
            ariaLabel="Open heatmap help"
            entries={HEATMAP_FAQ}
          />
        }
        pills={
          <TickerPills
            tickers={tickerList}
            active={ticker}
            onChange={handleTickerChange}
            groups={tickerGroups}
            draggableTickers={isPro}
            onTickerDragStart={() => setTickerDragging(true)}
          />
        }
      />

      {/* Search + favorites strip. Lives below the always-on EQUITIES
          pill row so the visual hierarchy reads:
            top  = "stuff we always poll"
            mid  = "everything else, type or pick"
          Recent (last 10 search picks) and favorites are populated
          via useRecentTickers + persisted to localStorage. State for
          searched symbols is created lazily on the backend — nothing
          gets cached until you actually open it, so adding hundreds
          of names to the universe is cost-free until they're viewed. */}
      <div className="gv-symbol-strip">
        <SymbolSearch onPick={handleSearchPick} />
        {(favorites.length > 0 || recent.length > 0) && (
          <div className="gv-symbol-strip-recents">
            {favorites.map(sym => (
              /* Favorite chip — star prefix, double-click still un-favorites.
                 X removes from the recent list only; the favorite entry persists
                 so the user can re-discover it via the strip-star or double-click.
                 data-x-hover is toggled on the wrapper via onMouseEnter/Leave on
                 the X button — avoids :has() which needs Firefox 121+. */
              <div
                key={`fav-${sym}`}
                role="group"
                aria-label={`${sym}, favorited`}
                className={`gv-recent-chip gv-recent-chip--fav${ticker === sym ? ' active' : ''}`}
              >
                <button
                  type="button"
                  className="gv-recent-chip-label"
                  onClick={() => handleTickerChange(sym)}
                  onDoubleClick={() => toggleFavorite(sym)}
                  title="Double-click to un-favorite"
                  aria-label={`Switch to ${sym}`}
                  draggable={isPro || undefined}
                  onDragStart={isPro ? (e) => {
                    e.dataTransfer.setData('text/gv-ticker', sym);
                    e.dataTransfer.effectAllowed = 'copy';
                    setTickerDragging(true);
                  } : undefined}
                >
                  <span aria-hidden="true">★</span> {sym}
                </button>
                <button
                  type="button"
                  className="gv-recent-chip-remove"
                  aria-label={`Remove ${sym} from recent`}
                  title={`Remove ${sym} from recent`}
                  onMouseEnter={e => (e.currentTarget.closest('.gv-recent-chip') as HTMLElement | null)?.setAttribute('data-x-hover', '')}
                  onMouseLeave={e => (e.currentTarget.closest('.gv-recent-chip') as HTMLElement | null)?.removeAttribute('data-x-hover')}
                  onClick={() => removeRecent(sym)}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
            ))}
            {recent
              .filter(sym => !favorites.includes(sym) && !alwaysOnSet.has(sym))
              .slice(0, 5)
              .map(sym => (
                /* Recent chip — X is visible only on chip hover (CSS opacity).
                   On touch devices the X is always visible (@media pointer:coarse).
                   data-x-hover on the wrapper triggers the red-tint via CSS attr
                   selector (Firefox-safe, no :has() required). */
                <div
                  key={`recent-${sym}`}
                  role="group"
                  aria-label={`${sym}, recent`}
                  className={`gv-recent-chip${ticker === sym ? ' active' : ''}`}
                >
                  <button
                    type="button"
                    className="gv-recent-chip-label"
                    onClick={() => handleTickerChange(sym)}
                    onDoubleClick={() => toggleFavorite(sym)}
                    title="Double-click to favorite"
                    aria-label={`Switch to ${sym}`}
                    draggable={isPro || undefined}
                    onDragStart={isPro ? (e) => {
                      e.dataTransfer.setData('text/gv-ticker', sym);
                      e.dataTransfer.effectAllowed = 'copy';
                      setTickerDragging(true);
                    } : undefined}
                  >
                    {sym}
                  </button>
                  <button
                    type="button"
                    className="gv-recent-chip-remove"
                    aria-label={`Remove ${sym} from recent`}
                    title={`Remove ${sym} from recent`}
                    onMouseEnter={e => (e.currentTarget.closest('.gv-recent-chip') as HTMLElement | null)?.setAttribute('data-x-hover', '')}
                    onMouseLeave={e => (e.currentTarget.closest('.gv-recent-chip') as HTMLElement | null)?.removeAttribute('data-x-hover')}
                    onClick={() => removeRecent(sym)}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
              ))}
          </div>
        )}
        {/* Star-toggle for the current ticker — handy when the user
            arrived via search and wants to keep it pinned. Only
            renders for non-always-on tickers (always-on are already
            visible in the pill row above). */}
        {!alwaysOnSet.has(ticker) && (
          <button
            type="button"
            className={`gv-symbol-strip-star${isFavorite(ticker) ? ' active' : ''}`}
            onClick={() => toggleFavorite(ticker)}
            aria-label={isFavorite(ticker) ? `Un-favorite ${ticker}` : `Favorite ${ticker}`}
            title={isFavorite(ticker) ? `Un-favorite ${ticker}` : `Favorite ${ticker}`}
          >
            {isFavorite(ticker) ? '★' : '☆'}
          </button>
        )}
      </div>

      <div className="gv-options-body">
        {/* Sticky live-price banner — moved from page bottom so the
            spot reference is always visible when scrolling through the
            heatmap rows. Reads bigger than the inline "Spot" row in
            the analytics card; treated as the primary anchor. */}
        <div className="gv-spot-strip gv-spot-strip--top" aria-live="polite">
          <span className="gv-spot-ticker">{ticker}</span>
          {spotPrice !== null ? (
            <>
              <span className="gv-spot-value">${spotPrice.toFixed(2)}</span>
              <span className={`gv-live-badge gv-live-badge--mini ${liveStatus === 'STREAMING' ? 'connected' : 'disconnected'}`}>
                <span className="gv-live-dot" />
                {liveStatus === 'STREAMING' ? 'LIVE' : liveStatus}
              </span>
              {!isMarketOpen && <span className="gv-market-closed">CLOSED</span>}
            </>
          ) : (
            <span className="gv-spot-loading">connecting…</span>
          )}
        </div>

        {!compareActive && <HeatmapPanels frame={activeFrame} />}
        {!compareActive && <StarNodesStrip frame={activeFrame} />}

        <HeatmapControls
          window={strikeWindow}
          onWindowChange={setStrikeWindow}
          expLimit={expLimit}
          onExpLimitChange={setExpLimit}
          viewMode={viewMode}
          onViewChange={setViewMode}
        />

        {/* ── Compare entry points (Pro) ──────────────────────────────
            Desktop: a dashed drop zone appears while a pill/chip drag is
            in flight (and stays visible in compare mode while there is a
            free slot). Mobile: drag-and-drop is unreliable — a picker
            <select> adds panels instead. */}
        {isPro && !isMobile && (tickerDragging || (compareActive && compareTickers.length < 3)) && (
          <div
            className={`gv-dropzone${tickerDragging ? ' is-armed' : ''}`}
            onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
            onDrop={e => {
              e.preventDefault();
              const sym = e.dataTransfer.getData('text/gv-ticker');
              if (sym) addCompare(sym);
              setTickerDragging(false);
            }}
          >
            {compareActive
              ? 'Drop a ticker to add a third panel'
              : `Drop a ticker here to compare with ${ticker}`}
          </div>
        )}
        {isPro && isMobile && (!compareActive || compareTickers.length < 3) && (
          <div className="gv-compare-picker-row">
            <select
              className="gv-compare-picker"
              value=""
              aria-label="Add a ticker to compare"
              onChange={e => { if (e.target.value) addCompare(e.target.value); e.target.value = ''; }}
            >
              <option value="">+ Compare…</option>
              {[...new Set([...tickerList, ...favorites, ...recent])]
                .filter(t => t !== ticker && !compareTickers.includes(t))
                .map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}

        {compareActive ? (
          <ComparePanels
            tickers={compareTickers}
            viewMode={viewMode}
            window={strikeWindow}
            onRemove={removeCompare}
            onReorder={persistCompare}
          />
        ) : isMobile ? (
          <HeatmapMobile data={heatmapData} viewMode={viewMode} flipPoint={flipPoint} isConnected={isConnected} />
        ) : heatmapData ? (
          <HeatmapTable
            key={`table-${density}`}
            data={heatmapData}
            viewMode={viewMode}
            flipPoint={flipPoint}
            ticker={ticker}
            window={strikeWindow}
          />
        ) : (
          <HeatmapEmpty />
        )}

        <Disclaimer>
          <strong>OPTIONS DATA NOTICE:</strong> snapshots are taken every {latestFrame ? '30 seconds' : 'few seconds'} during market hours and the
          provider can lag by up to a minute. Verify all numbers against your broker before acting.
        </Disclaimer>
      </div>
    </div>
  );
};

export default Options;
